import { readPatientUuidFromLocation } from '../../auth/patientContext';
import { useState, useCallback, useMemo, useEffect } from 'react';

// Extract top-level JSON objects from a buffer.
// Unlike a naive brace counter this skips braces inside JSON strings,
// so nested JSON in chunk content (e.g. chart specs) won't confuse it.
// Returns { objects, remainder }.
function extractJsonObjects(buffer) {
  const objects = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < buffer.length; i++) {
    const ch = buffer[i];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (ch === '\\' && inString) {
      escaped = true;
      continue;
    }

    if (ch === '"') {
      inString = !inString;
      continue;
    }

    if (inString) continue;

    if (ch === '{') {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        try {
          const obj = JSON.parse(buffer.substring(start, i + 1));
          objects.push({ value: obj, end: i + 1 });
        } catch (e) {
          // skip malformed object
        }
        start = -1;
      }
    }
  }

  // Keep unparsed tail so partially-received objects survive to the next chunk
  const lastEnd = objects.length > 0 ? objects[objects.length - 1].end : 0;
  return {
    objects: objects.map(o => o.value),
    remainder: buffer.substring(lastEnd),
  };
}

// Map tool names to user-friendly messages
const TOOL_MESSAGES = {
  get_health_data: '🔍 Analyzing your health data...',
  get_glucose_readings: '📊 Fetching your glucose readings...',
  get_activity_data: '🏃 Retrieving your activity data...',
  get_medication_info: '💊 Looking up your medication information...',
  analyze_trends: '📈 Analyzing trends in your data...',
  default: '⚙️ Processing your request...',
};

// API function to fetch session messages
export const fetchSessionMessages = async (sessionId) => {
  try {
    const token = localStorage.getItem('user');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Get the patient UUID from URL parameter - same as chat requests
    const patientUuid = readPatientUuidFromLocation();


    const requestBody = {
      action: 'get_session_messages',
      sessionId: sessionId,
      user_uuid: patientUuid, // Include patient UUID from URL parameter
      target_user_id: patientUuid // Also try target_user_id in case backend expects this
    };


    const response = await fetch('/api/ai-health-insights/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to load messages');
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching session messages:', error);
    throw error;
  }
};

// API function to fetch suggested prompts
export const fetchSuggestedPrompts = async () => {
  try {
    const token = localStorage.getItem('user');
    if (!token) {
      throw new Error('No authentication token found');
    }


    const response = await fetch('/api/ai-health-insights/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ action: 'get_suggested_prompts' }),
    });


    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ HTTP Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
    }


    // Parse the last non-empty line
    const lines = buffer.split('\n').filter(l => l.trim());
    if (lines.length === 0) {
      throw new Error('Empty response from suggested prompts endpoint');
    }

    const parsed = JSON.parse(lines[lines.length - 1]);

    if (parsed.type === 'error') {
      throw new Error(parsed.error || 'Failed to fetch suggested prompts');
    }

    // Sort by display_order and return
    const prompts = parsed.data || [];
    const sorted = prompts.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    return sorted;
  } catch (error) {
    console.error('Error fetching suggested prompts:', error);
    throw error;
  }
};

// Derive a per-patient sessionStorage key so sessions can't leak across patients.
const getSessionStorageKey = () => {
  const params = new URLSearchParams(window.location.search);
  const patientUuid = readPatientUuidFromLocation();
  return patientUuid ? `chatbot_session_id_${patientUuid}` : 'chatbot_session_id';
};

export const useCGMPalRuntime = () => {
  const [messages, setMessages] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  // Initialise from sessionStorage so the session survives component remounts
  // (e.g. Mantine Drawer unmounting its content on close).
  const [sessionId, setSessionIdState] = useState(
    () => sessionStorage.getItem(getSessionStorageKey()) || null
  );
  const [conversationHistory, setConversationHistory] = useState([]);

  // Keep sessionStorage in sync whenever sessionId changes.
  useEffect(() => {
    const key = getSessionStorageKey();
    if (sessionId) {
      sessionStorage.setItem(key, sessionId);
    } else {
      sessionStorage.removeItem(key);
    }
  }, [sessionId]);

  // Wrap the raw setter so all callers automatically sync sessionStorage.
  const setSessionId = useCallback((id) => {
    setSessionIdState(id);
  }, []);

  const append = useCallback(async (message) => {
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message.content,
      createdAt: new Date(),
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setIsRunning(true);

    let currentAssistantId = (Date.now() + 1).toString();
    let currentAssistantContent = '';
    let fullAssistantContent = '';
    let currentAssistantCreated = false;
    let toolCallId = null;

    try {
      const token = localStorage.getItem('user');
      
      // Get the patient UUID from URL parameter
      const patientUuid = readPatientUuidFromLocation();

      if (!token) {
        throw new Error('No authentication token found');
      }

      // Get user's timezone (e.g., "America/New_York")
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Get timezone offset in minutes (e.g., -300 for EST)
      const timezoneOffset = new Date().getTimezoneOffset();

      // Read sessionId from sessionStorage to avoid stale closure values.
      const currentSessionId = sessionStorage.getItem(getSessionStorageKey()) || sessionId;

      const response = await fetch('/api/ai-health-insights/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: message.content,
          user_uuid: patientUuid,
          sessionId: currentSessionId,
          timezone: userTimezone,
          timezoneOffset: timezoneOffset,
        }),
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let rawBuffer = '';
      let waitingForResponse = false;

      while (true) {
        const { done, value } = await reader.read();

        if (value) {
          rawBuffer += decoder.decode(value, { stream: true });
        }

        const { objects, remainder } = extractJsonObjects(rawBuffer);
        rawBuffer = remainder;

        for (const parsed of objects) {
          // Skip the status/metadata line
          if (parsed.statusCode !== undefined) continue;

          if (parsed.type === 'session') {
            setSessionId(parsed.sessionId);

          } else if (parsed.type === 'tool_start') {
            // Tool call starting — insert tool status block after current messages
            const toolMessage = TOOL_MESSAGES[parsed.toolName] || TOOL_MESSAGES.default;
            toolCallId = `tool-${Date.now()}`;
            waitingForResponse = true;

            setMessages((prev) => [
              ...prev,
              {
                id: toolCallId,
                role: 'tool',
                toolName: parsed.toolName,
                status: 'loading',
                message: toolMessage,
                createdAt: new Date(),
              },
            ]);

          } else if (parsed.type === 'tool_complete') {
            // Tool done, but keep "loading" until first chunk arrives

          } else if (parsed.type === 'chunk') {
            // If we were waiting for chunks after a tool call,
            // mark the tool as success and start a fresh assistant bubble
            if (waitingForResponse) {
              waitingForResponse = false;

              // Flip tool status to success
              const finishedToolId = toolCallId;
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findIndex(m => m.id === finishedToolId);
                if (idx >= 0) {
                  updated[idx] = {
                    ...updated[idx],
                    status: 'success',
                    message: 'Data fetched successfully',
                  };
                }
                return updated;
              });

              // Start a brand-new assistant message for post-tool content
              currentAssistantId = `assistant-${Date.now()}`;
              currentAssistantContent = '';
              currentAssistantCreated = false;
            }

            currentAssistantContent += parsed.content;
            fullAssistantContent += parsed.content;

            if (!currentAssistantCreated) {
              // Create new assistant bubble
              setMessages((prev) => [
                ...prev,
                {
                  id: currentAssistantId,
                  role: 'assistant',
                  content: currentAssistantContent,
                  followupQuestions: [],
                  createdAt: new Date(),
                },
              ]);
              currentAssistantCreated = true;
            } else {
              // Update existing assistant bubble
              setMessages((prev) => {
                const index = prev.findIndex(m => m.id === currentAssistantId);
                if (index >= 0) {
                  const updated = [...prev];
                  updated[index] = {
                    ...updated[index],
                    content: currentAssistantContent,
                  };
                  return updated;
                }
                return prev;
              });
            }

          } else if (parsed.type === 'followup_questions') {
            // Add follow-up questions to the current assistant message
            if (currentAssistantCreated) {
              setMessages((prev) => {
                const index = prev.findIndex(m => m.id === currentAssistantId);
                if (index >= 0) {
                  const updated = [...prev];
                  updated[index] = {
                    ...updated[index],
                    followupQuestions: parsed.questions || [],
                  };
                  return updated;
                }
                return prev;
              });
            }

          } else if (parsed.type === 'done') {
            setConversationHistory((prev) => [
              ...prev,
              { role: 'user', content: message.content },
              { role: 'assistant', content: fullAssistantContent },
            ]);

          } else if (parsed.type === 'error') {
            throw new Error(parsed.message || 'API error occurred');
          }
        }

        if (done) break;
      }

      setIsRunning(false);

    } catch (error) {
      console.error('Error in chat:', error);
      
      // Mark tool call as failed if one was in progress
      if (toolCallId) {
        setMessages((prev) => {
          const updated = [...prev];
          const toolIndex = updated.findIndex(m => m.id === toolCallId);
          if (toolIndex >= 0) {
            updated[toolIndex] = {
              ...updated[toolIndex],
              status: 'error',
              message: 'Failed to fetch data',
            };
          }
          return updated;
        });
      }
      
      const errorMsgId = `error-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: errorMsgId,
          role: 'assistant',
          content: `Sorry, I encountered an error: ${error.message}`,
          createdAt: new Date(),
        },
      ]);
      setIsRunning(false);
    }
  }, [sessionId, conversationHistory]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setConversationHistory([]);
  }, []);

  const loadHistoricalSession = useCallback(async (historicalSessionId) => {
    try {
      setIsRunning(true);
      const historicalMessages = await fetchSessionMessages(historicalSessionId);
      
      // Convert API messages to UI message format
      const formattedMessages = historicalMessages.map((msg, index) => ({
        id: `${historicalSessionId}-${index}`,
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.message,
        createdAt: new Date(msg.created_at * 1000),
      }));
      
      setMessages(formattedMessages);
      setSessionId(historicalSessionId);
      
      // Rebuild conversation history for context
      const history = historicalMessages.map((msg) => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.message,
      }));
      setConversationHistory(history);
    } catch (error) {
      console.error('Error loading historical session:', error);
      // Show error message in chat
      setMessages([
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: `Sorry, I couldn't load that conversation: ${error.message}`,
          createdAt: new Date(),
        },
      ]);
    } finally {
      setIsRunning(false);
    }
  }, []);

  const runtime = useMemo(
    () => ({
      messages,
      isRunning,
      append,
      clearChat,
      loadHistoricalSession,
      sessionId,
      reload: () => {},
      cancel: () => {
        setIsRunning(false);
      },
    }),
    [messages, isRunning, append, clearChat, loadHistoricalSession, sessionId]
  );

  return runtime;
};
